/**
 * Presentation rules for step 3 (f10.6) -- labels, pills and the transformation
 * list. Nothing here decides anything: cleaning, resolution, lines, figures and
 * every capability come from the server. Shared wording helpers are step 1's.
 */

import type { KimbleReview } from '@/api/client';

export {
  hasStagedItems,
  isParsing,
  refusalMessage,
  severityPill,
} from '../usEngagements/usEngagementsView';
export { MONTH_LABELS } from '../billableDays/billableDaysView';

export const UNDECIDED = 'UNDECIDED';
export const MATCHED = 'MATCHED';
export const FIXED_UNIT = 'FIXED_UNIT';

const DECISION_PILLS: Readonly<Record<string, string>> = {
  MATCHED: 'pill-success',
  UNDECIDED: 'pill-warning',
  MAP: 'pill-info',
  EXCLUDE: 'pill-neutral',
  BLANK: 'pill-neutral',
};

export function decisionPill(code: string): string {
  return `badge ${DECISION_PILLS[code] ?? 'pill-neutral'}`;
}

export interface Transformation {
  ok: boolean;
  text: string;
}

function issue(review: KimbleReview, code: string) {
  return review.issues.items.find((item) => item.code === code);
}

/** "HTML table sniffed — the file is named .xls", said only when it is true. */
export function formatNote(review: KimbleReview): string | null {
  const name = review.batch.original_filename?.toLowerCase() ?? '';
  if (review.batch.detected_format === 'HTML_TABLE' && name.endsWith('.xls')) {
    return 'HTML table sniffed — the file is named .xls';
  }
  return null;
}

/** The "Transformations applied" list, stated from the batch's own facts (design D3/D10). */
export function transformations(review: KimbleReview): Transformation[] {
  const { batch, figures } = review;
  const list: Transformation[] = [
    { ok: true, text: 'Columns bound by header name — never by position' },
    { ok: true, text: 'Period Start Date read day-first; probability scaled ×0.01; level taken from K Profile' },
    { ok: true, text: 'Every amount kept with its own currency column — nothing converted' },
    { ok: true, text: 'Resource BU resolved through the maintained mapping table — never guessed' },
  ];
  if (review.batch.scope?.code && review.batch.business_unit) {
    list.push({
      ok: true,
      text: `Every row assigned to ${review.batch.business_unit.name}, chosen at upload`,
    });
  }
  const dropped = issue(review, 'rows_dropped');
  if (dropped) list.push({ ok: true, text: dropped.message });
  if (figures.unmapped_bu_values > 0) {
    list.push({
      ok: false,
      text: `${figures.unmapped_bu_values} Resource BU values matched no mapping (${figures.unmapped_bu_rows} rows)`,
    });
  }
  if (figures.unresolved_units > 0) {
    list.push({ ok: false, text: `${figures.unresolved_units} Proposal: Unit values matched no Business Unit` });
  }
  if (figures.elements_not_in_hierarchy > 0) {
    list.push({
      ok: false,
      text: `${figures.elements_not_in_hierarchy} elements are not in the hierarchy — their rows make no line`,
    });
  }
  if (batch.rejection_reason) list.push({ ok: false, text: batch.rejection_reason });
  return list;
}

/** `{USD: 3000}` -> "USD · 3,000 amounts". */
export function currencySummary(currencies: Readonly<Record<string, number>>): string {
  const entries = Object.entries(currencies);
  if (!entries.length) return '—';
  return entries.map(([code, count]) => `${code} · ${count.toLocaleString()} amounts`).join(', ');
}

/** A decimal string without trailing zeros beyond two places, for dense tables. */
export function compactDecimal(value: string): string {
  if (!value.includes('.')) return value;
  const trimmed = value.replace(/0+$/, '').replace(/\.$/, '');
  return trimmed === '-0' ? '0' : trimmed;
}
